/* eslint-disable id-length */
/* eslint-disable max-nested-callbacks */
import {
  BATCH_UPLOAD_SUCCESS,
  BATCH_UPLOAD_FAILURE,
  BATCH_UPLOAD_EDIT_LOAD_BATCH,
  BATCH_UPLOAD_EDIT_UPDATE_ROW,
  BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW
} from '@constants/action-types';
import axios from 'axios';
import { runSegmentGeocode } from '@actions/data-detail-actions';
import { downloadCSVFromURL } from '@actions/data-table-actions';
import { BASE_API_URL, getAPIRequestUrl } from '@constants/endpoints';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

const uploadSuccess = payload => ({
  type: BATCH_UPLOAD_SUCCESS,
  payload
});

const uploadError = error => ({
  type: BATCH_UPLOAD_FAILURE,
  payload: error
});

export const uploadBatch = (upload, onProgress, onFail) => {
  const url = getAPIRequestUrl('batch');
  const formData = new FormData();
  formData.append('file', upload.file);
  formData.append('active', upload.active);
  formData.append('status', upload.status);
  if (upload.layerType) {
    formData.append('layerType', upload.layerType);
    formData.append('type', 2);
  } else
    if (upload.entityType) {
      formData.append('entityType', upload.entityType);
      formData.append('type', 3);
    }
  const config = { onUploadProgress: onProgress };
  const request = axios.post(url, formData, config);
  return dispatch =>
    request.then(
      data => dispatch(uploadSuccess(data)),
      error => {
        if (onFail) {
          onFail(error);
        }
        return dispatch(uploadError(error));
      }
    );
};

// Download a CSV template for the specified entity type:
export const downloadTemplate = type => {
  const url = `${BASE_API_URL}/batch/template/${type}/`;
  const filename = `${type}-template.csv`;
  return dispatch => dispatch(downloadCSVFromURL(url, filename, type));
};

export const validateBatch = (file, type, onValidationResults) => {
  const url = `${BASE_API_URL}/batch/validate/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onValidationResults(data?.data);
    },
    error => {
      onValidationResults({
        errors: [
          `Validation failed: ${error}`
        ]
      });
    }
  );
};

export const getSchema = (file, type, onError, onSchemaResults) => {
  const url = `${BASE_API_URL}/batch/schema/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onSchemaResults(data?.data?.rows);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const convertToJSON = (file, type, fields, onError, onResults) => {
  const url = `${BASE_API_URL}/batch/tojson/${type}/`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fields', fields);
  formData.append('type', type);
  const request = axios.post(url, formData);
  request.then(
    data => {
      onResults(data?.data);
    },
    error => {
      onError(`Error while retrieving fields: ${error}`);
    }
  );
};

export const loadBatch = ({ fileId, rows, fileName, entityType }) => ({
  type: BATCH_UPLOAD_EDIT_LOAD_BATCH,
  payload: { fileId, rows, fileName, entityType }
});

export const updateRow = ({ fileId, rowId, updates, errors, segmentErrors }) => ({
  type: BATCH_UPLOAD_EDIT_UPDATE_ROW,
  payload: { fileId, rowId, updates, errors, segmentErrors }
});

export const toggleSkipRow = ({ fileId, rowId }) => ({
  type: BATCH_UPLOAD_EDIT_TOGGLE_SKIP_ROW,
  payload: { fileId, rowId }
});

// Run geocoding for address data in the rows uploaded via batch upload.
export const geocodeBatchRows = (fileId, rows, entityConfig) => dispatch => {
  // We needed a way to wait for window.google.map.xxxx data to appear,
  // since sometimes this code might run before the Google code is
  // available on the javascript window.xxxx namespace.
  const waitForGoogleMaps = callback => {
    const poll = () => {
      if (window.mapSetup) {
        callback();
      } else {
        setTimeout(poll, 100);  // Check every 100ms
      }
    };
    poll();
  };

  // Run the geocoding of rows in batches, with a delay (our backend can handle it
  // but I added this since I don't know if Google might rate limit us, if we
  // call geocodeByAddress() too often).
  const processGeocodingQueue = (rowsToProcess, batchSize = 10, delay = 1000) => {
    let i = 0;
    const processBatch = () => {
      const batch = rowsToProcess.slice(i, i + batchSize);
      if (batch.length === 0) {
        return;  // All done
      }

      batch.forEach(row => {
        const segments = row.data.segments || [];
        const segmentPromises = segments.map(segment => {
          if (segment.shape) {
            return Promise.resolve(segment);
          }

          // Special case for Soundtransit:
          if (entityConfig?.use_layer_geocoding) {
            const params = {
              on_street: segment.on_street,
              from_address: segment.from_address,
              from_street: segment.from_street,
              to_address: segment.to_address,
              to_street: segment.to_street,
              from_station: segment.from_station,
              to_station: segment.to_station,
              line_name: segment.line_name
            };

            return axios.get(`${BASE_API_URL}/street_segment/`, { params })
              .then(response => {
                const results = response.data;
                if (results.length > 0) {
                  const {feature, ...rest} = results[0];
                  return {
                    segment: {
                      ...segment,
                      ...rest,
                      shape: feature.geometry
                    },
                    error: null
                  };
                }
                return { segment, error: null };
              })
              .catch(error => {
                return {
                  segment,
                  error: {
                    shape: error?.response?.data?.error
                  }
                };
              });
          }

          const { display_from, display_to } = segment;

          const fromPromise = display_from ? geocodeByAddress(display_from).then(results => getLatLng(results[0]))
            .catch(() => null) : Promise.resolve(null);
          const toPromise = display_to ? geocodeByAddress(display_to).then(results => getLatLng(results[0]))
            .catch(() => null) : Promise.resolve(null);

          return Promise.all([fromPromise, toPromise]).then(([start, end]) => {
            // At least a start point is needed:
            if (start) {
              return runSegmentGeocode(start, end)
                .then(segmentWithShape => ({
                  segment: { ...segment, ...segmentWithShape },
                  error: null
                }))
                .catch(() => ({ segment, error: null }));
            }
            return { segment, error: null };
          })
            .catch(() => ({ segment, error: null }));
        });

        Promise.all(segmentPromises).then(updatedSegments => {
          const segmentsObj = updatedSegments.map(item => {
            const updatedSegment = item.segment;

            // If we specified from_station or to_station for geocoding,
            // but got back only coordinates without address info,
            // set the title to the station name
            if ((updatedSegment.from_station || updatedSegment.to_station) &&
                (!updatedSegment.on_street && !updatedSegment.from_address &&
                 !updatedSegment.from_street && !updatedSegment.to_address &&
                 !updatedSegment.to_street)) {
              const stationName = updatedSegment.from_station || updatedSegment.to_station;
              return {
                ...updatedSegment,
                title: stationName
              };
            }

            return updatedSegment;
          });
          const errors = updatedSegments.map(item => item.error);
          const hasActualErrors = errors.some(error => error !== null);
          const finalErrors = hasActualErrors ? errors : [];

          const updatedData = {
            // Only update segments, since row.data might have changed due to processing.
            segments: segmentsObj
          };
          dispatch(updateRow({
            fileId,
            rowId: row.id,
            updates: updatedData,
            // eslint-disable-next-line no-undefined
            segmentErrors: finalErrors.length > 0 ? finalErrors : undefined
          }));
        });
      });

      i += batchSize;
      if (i < rowsToProcess.length) {
        setTimeout(processBatch, delay);
      }
    };

    processBatch();
  };

  // Launch the geocoding:
  waitForGoogleMaps(() => {
    const rowsToGeocode = rows.filter(row => row.data.segments?.some(segment => !segment.shape));
    processGeocodingQueue(rowsToGeocode);
  });
};
